<template>
  <div>
    <h2 class="mb-4">
      <i class="fa-duotone fa-book text-primary fa-fw"></i>
      {{ translate("wiki") }}
    </h2>
    <CollectionView :endpoint="utilities.path('communities/@/modules/wiki/categories', communityId)" searchable v-model:reload="reload" :orderable="[{key:'name', name: translate('name')}]">
      <template v-slot:header>
        <div class="d-flex align-items-center justify-content-between">
          <h4 class="mb-0">{{ translate("categories") }}</h4>
          <ButtonView type="normal" color="primary" @click="creating = true">
            {{ translate("create") }}
          </ButtonView>
        </div>
      </template>
      <template v-slot:content="{ items }">
        <div class="card">
          <div class="table-responsive">
            <div class="table">
              <div class="table-header">
                <div class="table-header-cell">{{ translate("name") }}</div>
                <div class="table-header-cell">{{ translate("color") }}</div>
                <div class="table-header-cell">{{ translate("actions") }}</div>
              </div>
              <div class="table-body">
                <RouterLink class="table-row" v-for="item in items" :key="item.id" :to="{ name: 'community.modules.wiki.category', params: { categoryId: item.id }}">
                  <div class="table-column">
                    {{ item.name }}
                  </div>
                  <div class="table-column">
                    <i class="fas fa-circle fa-fw" :style="{ color: item.color }"></i>
                  </div>
                  <div class="table-column">
                    <div class="d-flex align-items-center g-2">
                      <ButtonView v-if="authorization.permission('modules.wiki.categories.edit')" type="soft" color="primary" size="small" @click.prevent="editing = item">
                        <i class="fas fa-edit"></i>
                      </ButtonView>
                      <ResourcePermissionModalView type="modules.wiki.category" :id="item.id"/>
                      <TargetedModalHttpFormView v-if="authorization.permission('modules.wiki.categories.edit')" size="small" :endpoint="utilities.path('modules/wiki/categories/@', item.id)" method="delete" @success="reload = true">
                        <template v-slot:target="{ show }">
                          <ButtonView type="soft" color="danger" size="small" @click.prevent="show">
                            <i class="fas fa-trash"></i>
                          </ButtonView>
                        </template>
                        <template v-slot:form="{ submit, loading, hide }">
                          <h2 class="mb-4">{{ item.name }}</h2>
                          <SpinnerView :active="loading" color="danger"/>
                          <div class="d-flex align-items-center g-4">
                            <ButtonView type="normal" color="danger" class="w-100" @click="submit">
                              {{ translate("delete") }}
                            </ButtonView>
                            <ButtonView type="normal" color="secondary" class="w-100" @click="hide">
                              {{ translate("cancel") }}
                            </ButtonView>
                          </div>
                        </template>
                      </TargetedModalHttpFormView>
                    </div>
                  </div>
                </RouterLink>
              </div>
            </div>
          </div>
        </div>
      </template>
    </CollectionView>
    <ModalHttpFormView :endpoint="creating ? utilities.path('communities/@/modules/wiki/categories', communityId) : utilities.path('modules/wiki/categories/@', editing?.id)" :method="creating ? 'post' : 'put'" :content="creating ? undefined : editing" :active="creating || editing !== undefined" @update:active="creating = false; editing = undefined" @success="reload = true">
      <template v-slot:form="{ content, errors, submit, loading }">
        <div class="d-flex flex-column g-4">
          <InputView type="text" :max="32" :value="content.name" @update:value="(content.slug ?? '') === utilities.slugify(content.name ?? '') ? content.slug = utilities.slugify($event) : undefined; content.name = $event" :title="translate('name')" :errors="errors.name" required/>
          <div>
            <InputView type="text" :max="32" :value="content.slug" @update:value="content.slug = utilities.slugify($event)" :title="translate('slug')" :errors="errors.slug" required>
              <template v-slot:extra>
                <div class="bg-secondary p-1 rounded-3 text-foreground">
                  a-z, -, 0-9
                </div>
              </template>
            </InputView>
            <div v-if="info.community && content.slug" class="text-muted small mt-2 ms-3">
              {{ info.community.domain ?? info.community.subdomain }}/wiki/{{ content.slug }}
            </div>
          </div>
          <InputView type="color" v-model:value="content.color" :placeholder="translate('color')" :title="translate('color')" :errors="errors.color" required/>
          <div class="d-flex align-items-center g-4">
            <ButtonView type="normal" color="primary" class="w-100" @click="submit">
              {{ translate("save") }}
            </ButtonView>
            <ButtonView type="normal" color="secondary" class="w-100" @click="creating = false; editing = undefined">
              {{ translate("cancel") }}
            </ButtonView>
          </div>
          <SpinnerView :active="loading" color="primary"/>
        </div>
      </template>
    </ModalHttpFormView>
  </div>
</template>

<script setup>
import {CollectionView, InputView, utilities, ButtonView, SpinnerView, ModalHttpFormView, TargetedModalHttpFormView} from "@/niftybit";
import ResourcePermissionModalView from "@/systems/modules/roles/ResourcePermissionModalView.vue";
import {ref} from "vue";
import authorization from "@/authorization";
import info from "../../../info";

defineProps({
  communityId: String
});

const reload = ref(false);
const creating = ref(false);
const editing = ref(undefined);

</script>